import { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import AppLayout from "../../components/AppLayout";
import API_BASE_URL from "../../config/api";
import {
  Box,
  Button,
  Card,
  CardContent,
  Typography,
  TextField,
  Grid,
  FormControl,
  FormLabel,
  RadioGroup,
  FormControlLabel,
  Radio,
  Divider,
  IconButton,
  CircularProgress,
  Stack,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import SaveIcon from "@mui/icons-material/Save";
import { toast } from "react-toastify";
import { SleeveSection, BustSection, LowerBodySection, LengthMeasurementsSection } from "../../components/MeasurementSections";

const fieldSx = {
  width: "100%",
  "& .MuiOutlinedInput-root": {
    width: "100%",
    backgroundColor: "#1a1a1a",
    borderRadius: 1,
    border: "1px solid rgba(196,165,116,0.06)",
    color: "#fff",
    "& fieldset": { borderColor: "transparent" },
    "&:hover fieldset": { borderColor: "rgba(196,165,116,0.12)" },
    "&.Mui-focused fieldset": { borderColor: "#C4A574" },
  },
  "& .MuiInputBase-input": { color: "#fff", width: "100%" },
  "& .MuiFormLabel-root": { color: "#C4A574" },
  "& .MuiFormHelperText-root": { color: "#bfa678" },
};

const gridContainerResponsive = {
  // ensure all .responsive Grid items become full width under 560px
  "& .responsive": {
    "@media (max-width:560px)": {
      flexBasis: "100% !important",
      maxWidth: "100% !important",
    },
  },
};

const sectionFieldsSx = {
  ...gridContainerResponsive,
  // apply same field styling to any TextField inside measurement sections
  "& .MuiTextField-root": {
    ...fieldSx,
    width: "100%",
  },
  // ensure small-screen full width for grid items inside imported sections
  "& .MuiGrid-item": {
    "@media (max-width:560px)": {
      flexBasis: "100% !important",
      maxWidth: "100% !important",
    },
  },
};

export default function CustomerMeasurementForm() {
  const navigate = useNavigate();
  const { tokenNo } = useParams();
  const isEdit = Boolean(tokenNo);
  const [loading, setLoading] = useState(isEdit);
  const [saving, setSaving] = useState(false);

  const [formData, setFormData] = useState({
    // Customer Details
    TokenNo: "", // Will be auto-generated for new customers
    CustomerName: "",
    PhoneNumber: "",
    EmailID: "",
    Birthday: "",
    Age: "",
    IsMarried: 0,
    AnniversaryDate: "",
    Profession: "Other",
    ProfessionOther: "",

    // Upper Body
    Shoulder: "",
    ShoulderFrontLength: "",
    ShoulderFrontWidth: "",
    ShoulderBackLength: "",
    ShoulderBackWidth: "",
    NeckStraightFront: "",
    NeckStraightBack: "",
    NeckSlightFront: "",
    NeckSlightBack: "",
    Collar: "",
    HalfStand: "",
    DesignerBackDeep: "",
    DesignerBackPack: "",
    DesignerBackDeepHighest: "",

    // Sleeves
    SleeveShortLength: "",
    SleeveShortRound: "",
    SleeveAboveElbowLength: "",
    SleeveAboveElbowRound: "",
    Sleeve34Length: "",
    Sleeve34Round: "",
    SleeveFullLength: "",
    SleeveFullRound: "",
    ArmholeRound: "",
    BicepsLength: "",
    BicepsRound: "",

    // Bust
    UpperBustLength: "",
    UpperBustRound: "",
    Gap: "",
    BustLength: "",
    BustRound: "",
    UnderBustLength: "",
    UnderBustRound: "",
    StomachLength: "",
    StomachRound: "",
    LowerWaistLength: "",
    LowerWaistRound: "",
    HipLength: "",
    HipRound: "",

    // Lower Body
    LowerTotalLength: "",
    PlusHeels: "",
    LowerDescription: "",
    ThighsLength: "",
    ThighsRound: "",
    KneesLength: "",
    KneesRound: "",
    CalfLength: "",
    CalfRound: "",
    BottomLength: "",
    BottomRound: "",
    SeatLength: "",
    SeatRound: "",
    AnkleLength: "",
    AnkleRound: "",

    // Length Measurements
    AboveKnee: "",
    BelowKnee: "",
    MiddleFloorLength: "",
    FullLength: "",
    AnkleLengthMeasure: "",
    FloorLength: "",
    PlusHeelsLength: "",

    // Additional
    FittingPreference: "Regular",
    SpecialNotes: "",
  });

  useEffect(() => {
    if (isEdit) {
      fetchCustomer();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tokenNo]);

  const fetchCustomer = async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await fetch(
        `${API_BASE_URL}/api/customers/token/${tokenNo}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      if (response.ok) {
        const result = await response.json();
        setFormData({
          ...result.data,
          Birthday: result.data.Birthday ? result.data.Birthday.split("T")[0] : "",
          AnniversaryDate: result.data.AnniversaryDate ? result.data.AnniversaryDate.split("T")[0] : "",
        });
      } else {
        toast.error("Failed to fetch customer data");
        navigate("/admin/customers");
      }
    } catch (error) {
      console.error("Error fetching customer:", error);
      toast.error("Failed to load customer data");
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    
    // Auto-calculate age when birthday changes
    if (field === "Birthday" && value) {
      const age = calculateAge(value);
      setFormData((prev) => ({ ...prev, Age: age.toString() }));
    }
  };

  // Calculate age from birthday
  const calculateAge = (birthday) => {
    if (!birthday) return "";
    
    const birthDate = new Date(birthday);
    const today = new Date();
    let age = today.getFullYear() - birthDate.getFullYear();
    const monthDiff = today.getMonth() - birthDate.getMonth();
    
    // Adjust age if birthday hasn't occurred this year yet
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
      age--;
    }
    
    return age;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validation
    if (!formData.CustomerName || !formData.PhoneNumber) {
      toast.error("Name and Phone Number are required");
      return;
    }

    // Phone number validation
    const phoneRegex = /^[6-9]\d{9}$/;
    if (!phoneRegex.test(formData.PhoneNumber)) {
      toast.error("Please enter a valid 10-digit phone number starting with 6-9");
      return;
    }

    // Validate token number for new customers - not needed anymore as it's auto-generated
    // if (!isEdit && (!formData.TokenNo || formData.TokenNo.trim() === "")) {
    //   toast.error("Token Number is required");
    //   return;
    // }

    // Check if manually entered token number already exists (for new customers only) - not needed anymore
    // if (!isEdit && formData.TokenNo && formData.TokenNo.trim() !== "") {
    //   try {
    //     const token = localStorage.getItem("token");
    //     const checkResponse = await fetch(
    //       `${API_BASE_URL}/api/customers/token/${formData.TokenNo}`,
    //       {
    //         headers: { Authorization: `Bearer ${token}` },
    //       }
    //     );

    //     if (checkResponse.ok) {
    //       toast.error(`Token number ${formData.TokenNo} already exists. Please choose a different one.`);
    //       return;
    //     }
    //   } catch (error) {
    //     // If error is 404, token doesn't exist, which is good
    //     // Continue with creation
    //   }
    // }

    try {
      setSaving(true);
      const token = localStorage.getItem("token");
      const url = isEdit
        ? `${API_BASE_URL}/api/customers/${tokenNo}`
        : `${API_BASE_URL}/api/customers`;

      const response = await fetch(url, {
        method: isEdit ? "PUT" : "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(formData),
      });

      if (response.ok) {
        const result = await response.json();
        toast.success(`Customer ${isEdit ? "updated" : "created"} successfully`);
        if (!isEdit && result.tokenNo) {
          toast.info(`Auto-generated Token Number: ${result.tokenNo}`);
        }
        navigate("/admin/customers");
      } else {
        const error = await response.json();
        toast.error(error.message || "Failed to save customer");
      }
    } catch (error) {
      console.error("Error saving customer:", error);
      toast.error("Failed to save customer");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <AppLayout>
        <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: "60vh", background: "linear-gradient(135deg, #1a1a1a 0%, #2d2d2d 100%)" }}>
          <CircularProgress sx={{ color: "#C4A574" }} />
        </Box>
      </AppLayout>
    );
  }

  return (
    <AppLayout>
      <Box sx={{ minHeight: "calc(100vh - 80px)", background: "linear-gradient(135deg, #1a1a1a 0%, #2d2d2d 100%)", p: { xs: 2, md: 4 } }}>
        <Box sx={{ maxWidth: 1200, mx: "auto" }}>
          <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 3, gap: 2, flexWrap: "wrap" }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
              <IconButton
                onClick={() => navigate("/admin/customers")}
                sx={{ bgcolor: "#C4A574", color: "#000", "&:hover": { bgcolor: "#B39562" } }}
              >
                <ArrowBackIcon />
              </IconButton>
              <Typography variant="h5" sx={{ fontWeight: 800, color: "#fff", letterSpacing: 1 }}>
                {isEdit ? "Edit Customer Measurement" : "New Customer Measurement"}
              </Typography>
            </Box>
          </Stack>

          <Box component="form" id="measurement-form" onSubmit={handleSubmit}>
            {/* Customer Details */}
            <Card sx={{ mb: 3, bgcolor: "#242424", border: "1px solid rgba(255,255,255,0.03)", borderRadius: 2 }}>
              <CardContent>
                <Typography variant="h6" sx={{ fontWeight: 800, color: "#C4A574", mb: 2 }}>
                  SECTION 1 — CUSTOMER DETAILS
                </Typography>
                <Divider sx={{ borderColor: "rgba(255,255,255,0.04)", mb: 3 }} />

                <Grid container spacing={2} sx={gridContainerResponsive} justifyContent="center">
                  {!isEdit && (
                    <Grid item xs={12} sm={6} md={4} className="responsive">
                      <TextField
                        fullWidth
                        label="Token No"
                        value="Auto-generated"
                        disabled
                        sx={{ 
                          ...fieldSx,
                          "& .MuiInputBase-input": { color: "#bbb" }
                        }}
                        helperText="Token number will be automatically assigned"
                      />
                    </Grid>
                  )}
                  {isEdit && (
                    <Grid item xs={12} sm={6} md={4} className="responsive">
                      <TextField fullWidth label="Token No" value={tokenNo} disabled sx={{ ...fieldSx, "& .MuiInputBase-input": { color: "#bbb" } }} />
                    </Grid>
                  )}

                  <Grid item xs={12} sm={6} md={4} className="responsive">
                    <TextField fullWidth required label="Name" value={formData.CustomerName} onChange={(e) => handleChange("CustomerName", e.target.value)} sx={fieldSx} />
                  </Grid>

                  <Grid item xs={12} sm={6} md={4} className="responsive">
                    <TextField
                      fullWidth
                      required
                      label="Phone Number"
                      value={formData.PhoneNumber}
                      onChange={(e) => {
                        const value = e.target.value.replace(/\D/g, "");
                        if (value.length <= 10) handleChange("PhoneNumber", value);
                      }}
                      inputProps={{ maxLength: 10 }}
                      helperText="10-digit mobile number"
                      sx={fieldSx}
                    />
                  </Grid>

                  <Grid item xs={12} sm={6} md={4} className="responsive">
                    <TextField fullWidth label="Email ID" type="email" value={formData.EmailID} onChange={(e) => handleChange("EmailID", e.target.value)} sx={fieldSx} />
                  </Grid>

                  {/* Birthday and Anniversary - ensure same width and responsive */}
                  <Grid item xs={12} sm={6} md={4} className="responsive">
                    <TextField fullWidth label="Birthday" type="date" InputLabelProps={{ shrink: true }} value={formData.Birthday} onChange={(e) => handleChange("Birthday", e.target.value)} sx={fieldSx} />
                  </Grid>

                  <Grid item xs={12} sm={6} md={4} className="responsive">
                    <TextField fullWidth label="Age" value={formData.Age} InputProps={{ readOnly: true }} sx={{ ...fieldSx, "& .MuiInputBase-input.Mui-disabled": { WebkitTextFillColor: "#ccc" } }} />
                  </Grid>

                  <Grid item xs={12} sm={6} md={4} className="responsive">
                    <FormControl component="fieldset">
                      <FormLabel sx={{ color: "#C4A574", fontWeight: 700 }}>Marital Status</FormLabel>
                      <RadioGroup row value={formData.IsMarried.toString()} onChange={(e) => handleChange("IsMarried", parseInt(e.target.value))}>
                        <FormControlLabel value="0" control={<Radio sx={{ color: "#C4A574" }} />} label={<Typography sx={{ color: "#bbb" }}>No</Typography>} />
                        <FormControlLabel value="1" control={<Radio sx={{ color: "#C4A574" }} />} label={<Typography sx={{ color: "#bbb" }}>Yes</Typography>} />
                      </RadioGroup>
                    </FormControl>
                  </Grid>

                  {/* Anniversary shown only when married */}
                  {formData.IsMarried === 1 && (
                    <Grid item xs={12} sm={6} md={4} className="responsive">
                      <TextField fullWidth label="Anniversary Date" type="date" InputLabelProps={{ shrink: true }} value={formData.AnniversaryDate} onChange={(e) => handleChange("AnniversaryDate", e.target.value)} sx={fieldSx} />
                    </Grid>
                  )}

                  <Grid item xs={12} sm={6} md={4} className="responsive">
                    <FormControl component="fieldset">
                      <FormLabel sx={{ color: "#C4A574", fontWeight: 700 }}>Profession</FormLabel>
                      <RadioGroup value={formData.Profession} onChange={(e) => handleChange("Profession", e.target.value)} row>
                        <FormControlLabel value="Student" control={<Radio sx={{ color: "#C4A574" }} />} label={<Typography sx={{ color: "#bbb" }}>Student</Typography>} />
                        <FormControlLabel value="Housewife" control={<Radio sx={{ color: "#C4A574" }} />} label={<Typography sx={{ color: "#bbb" }}>Housewife</Typography>} />
                        <FormControlLabel value="Other" control={<Radio sx={{ color: "#C4A574" }} />} label={<Typography sx={{ color: "#bbb" }}>Other</Typography>} />
                      </RadioGroup>
                    </FormControl>
                  </Grid>

                  <Grid item xs={12} sm={6} md={4} className="responsive">
                    {formData.Profession === "Other" && (
                      <TextField fullWidth label="Specify Profession" value={formData.ProfessionOther} onChange={(e) => handleChange("ProfessionOther", e.target.value)} sx={fieldSx} />
                    )}
                  </Grid>
                </Grid>
              </CardContent>
            </Card>

            {/* Upper Body */}
            <Card sx={{ mb: 3, bgcolor: "#242424", border: "1px solid rgba(255,255,255,0.03)", borderRadius: 2 }}>
              <CardContent>
                <Typography variant="h6" sx={{ fontWeight: 800, color: "#C4A574", mb: 2 }}>
                  SECTION 2A — UPPER BODY MEASUREMENTS (in inches)
                </Typography>
                <Divider sx={{ borderColor: "rgba(255,255,255,0.04)", mb: 3 }} />

                <Grid container spacing={2} sx={gridContainerResponsive}>
                  {/* Headings forced to full width */}
                  <Grid item xs={12} className="responsive" sx={{width: '100%'}}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 700, color: "#bbb", mb: 1 }}>
                      Shoulder
                    </Typography>
                  </Grid>

                  <Grid item xs={12} sm={6} md={3} className="responsive">
                    <TextField fullWidth size="small" label="Shoulder" type="number" inputProps={{ step: "0.01" }} value={formData.Shoulder} onChange={(e) => handleChange("Shoulder", e.target.value)} sx={fieldSx} />
                  </Grid>
                  <Grid item xs={12} sm={6} md={3} className="responsive">
                    <TextField fullWidth size="small" label="Front Length" type="number" inputProps={{ step: "0.01" }} value={formData.ShoulderFrontLength} onChange={(e) => handleChange("ShoulderFrontLength", e.target.value)} sx={fieldSx} />
                  </Grid>
                  <Grid item xs={12} sm={6} md={3} className="responsive">
                    <TextField fullWidth size="small" label="Front Width" type="number" inputProps={{ step: "0.01" }} value={formData.ShoulderFrontWidth} onChange={(e) => handleChange("ShoulderFrontWidth", e.target.value)} sx={fieldSx} />
                  </Grid>
                  <Grid item xs={12} sm={6} md={3} className="responsive">
                    <TextField fullWidth size="small" label="Back Length" type="number" inputProps={{ step: "0.01" }} value={formData.ShoulderBackLength} onChange={(e) => handleChange("ShoulderBackLength", e.target.value)} sx={fieldSx} />
                  </Grid>
                  <Grid item xs={12} sm={6} md={3} className="responsive">
                    <TextField fullWidth size="small" label="Back Width" type="number" inputProps={{ step: "0.01" }} value={formData.ShoulderBackWidth} onChange={(e) => handleChange("ShoulderBackWidth", e.target.value)} sx={fieldSx} />
                  </Grid>

                  <Grid item xs={12} className="responsive" sx={{ mt: 2 }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 700, color: "#bbb", mb: 1 }}>
                      Neck
                    </Typography>
                  </Grid>

                  <Grid item xs={12} sm={6} md={3} className="responsive">
                    <TextField fullWidth size="small" label="Neck Straight Front" type="number" inputProps={{ step: "0.01" }} value={formData.NeckStraightFront} onChange={(e) => handleChange("NeckStraightFront", e.target.value)} sx={fieldSx} />
                  </Grid>
                  <Grid item xs={12} sm={6} md={3} className="responsive">
                    <TextField fullWidth size="small" label="Neck Straight Back" type="number" inputProps={{ step: "0.01" }} value={formData.NeckStraightBack} onChange={(e) => handleChange("NeckStraightBack", e.target.value)} sx={fieldSx} />
                  </Grid>
                  <Grid item xs={12} sm={6} md={3} className="responsive">
                    <TextField fullWidth size="small" label="Neck Slight Front" type="number" inputProps={{ step: "0.01" }} value={formData.NeckSlightFront} onChange={(e) => handleChange("NeckSlightFront", e.target.value)} sx={fieldSx} />
                  </Grid>
                  <Grid item xs={12} sm={6} md={3} className="responsive">
                    <TextField fullWidth size="small" label="Neck Slight Back" type="number" inputProps={{ step: "0.01" }} value={formData.NeckSlightBack} onChange={(e) => handleChange("NeckSlightBack", e.target.value)} sx={fieldSx} />
                  </Grid>

                  <Grid item xs={12} sm={6} md={3} className="responsive">
                    <TextField fullWidth size="small" label="Collar" type="number" inputProps={{ step: "0.01" }} value={formData.Collar} onChange={(e) => handleChange("Collar", e.target.value)} sx={fieldSx} />
                  </Grid>
                  <Grid item xs={12} sm={6} md={3} className="responsive">
                    <TextField fullWidth size="small" label="Half Stand" type="number" inputProps={{ step: "0.01" }} value={formData.HalfStand} onChange={(e) => handleChange("HalfStand", e.target.value)} sx={fieldSx} />
                  </Grid>

                  <Grid item xs={12} className="responsive" sx={{ mt: 2 }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 700, color: "#bbb", mb: 1 , width: '100%'}}>
                      Special Designer Back
                    </Typography>
                  </Grid>

                  <Grid item xs={12} sm={6} md={3} className="responsive">
                    <TextField fullWidth size="small" label="Deep" type="number" inputProps={{ step: "0.01" }} value={formData.DesignerBackDeep} onChange={(e) => handleChange("DesignerBackDeep", e.target.value)} sx={fieldSx} />
                  </Grid>
                  <Grid item xs={12} sm={6} md={3} className="responsive">
                    <TextField fullWidth size="small" label="Pack" type="number" inputProps={{ step: "0.01" }} value={formData.DesignerBackPack} onChange={(e) => handleChange("DesignerBackPack", e.target.value)} sx={fieldSx} />
                  </Grid>
                  <Grid item xs={12} sm={6} md={3} className="responsive">
                    <TextField fullWidth size="small" label="Deep (Highest)" type="number" inputProps={{ step: "0.01" }} value={formData.DesignerBackDeepHighest} onChange={(e) => handleChange("DesignerBackDeepHighest", e.target.value)} sx={fieldSx} />
                  </Grid>
                </Grid>
              </CardContent>
            </Card>

            {/* Sleeves */}
            <Card sx={{ mb: 3, bgcolor: "#242424", border: "1px solid rgba(255,255,255,0.03)", borderRadius: 2,  }}>
              <CardContent>
                <Typography variant="h6" sx={{ fontWeight: 800, color: "#C4A574", mb: 2 }}>
                  SLEEVE MEASUREMENTS
                </Typography>
                <Divider sx={{ borderColor: "rgba(255,255,255,0.04)", mb: 3 }} />
                <Grid container spacing={2} sx={{sectionFieldsSx, justifyContent: 'center'}}>
                  <SleeveSection formData={formData} handleChange={handleChange} />
                </Grid>
              </CardContent>
            </Card>

            {/* Bust & Waist */}
            <Card sx={{ mb: 3, bgcolor: "#242424", border: "1px solid rgba(255,255,255,0.03)", borderRadius: 2 }}>
              <CardContent>
                <Typography variant="h6" sx={{ fontWeight: 800, color: "#C4A574", mb: 2 }}>
                  BUST & WAIST MEASUREMENTS
                </Typography>
                <Divider sx={{ borderColor: "rgba(255,255,255,0.04)", mb: 3 }} />
                <Grid container spacing={2} sx={{sectionFieldsSx, justifyContent: 'center'}}>
                  <BustSection formData={formData} handleChange={handleChange} />
                </Grid>
              </CardContent>
            </Card>

            {/* Lower Body */}
            <Card sx={{ mb: 3, bgcolor: "#242424", border: "1px solid rgba(255,255,255,0.03)", borderRadius: 2 }}>
              <CardContent>
                <Typography variant="h6" sx={{ fontWeight: 800, color: "#C4A574", mb: 2 }}>
                  SECTION 2B — LOWER BODY MEASUREMENTS
                </Typography>
                <Divider sx={{ borderColor: "rgba(255,255,255,0.04)", mb: 3 }} />
                <Grid container spacing={2} sx={{sectionFieldsSx, justifyContent: 'center'}}>
                  <LowerBodySection formData={formData} handleChange={handleChange} />
                </Grid>
              </CardContent>
            </Card>

            {/* Lengths */}
            <Card sx={{ mb: 3, bgcolor: "#242424", border: "1px solid rgba(255,255,255,0.03)", borderRadius: 2 }}>
              <CardContent>
                <Typography variant="h6" sx={{ fontWeight: 800, color: "#C4A574", mb: 2 }}>
                  SECTION 2C — LENGTH MEASUREMENTS
                </Typography>
                <Divider sx={{ borderColor: "rgba(255,255,255,0.04)", mb: 3 }} />
                <Grid container spacing={2} sx={{sectionFieldsSx, justifyContent: 'center'}}>
                  <LengthMeasurementsSection formData={formData} handleChange={handleChange} />
                </Grid>
              </CardContent>
            </Card>

            {/* Additional */}
            <Card sx={{ mb: 3, bgcolor: "#242424", border: "1px solid rgba(255,255,255,0.03)", borderRadius: 2 }}>
              <CardContent>
                <Typography variant="h6" sx={{ fontWeight: 800, color: "#C4A574", mb: 2 }}>
                  SECTION 2D — ADDITIONAL NOTES
                </Typography>
                <Divider sx={{ borderColor: "rgba(255,255,255,0.04)", mb: 3 }} />
                <Grid container spacing={2} sx={gridContainerResponsive}>
                  <Grid item xs={12} className="responsive">
                    <FormControl component="fieldset">
                      <FormLabel sx={{ color: "#C4A574", fontWeight: 700 }}>Fitting Preference</FormLabel>
                      <RadioGroup row value={formData.FittingPreference} onChange={(e) => handleChange("FittingPreference", e.target.value)}>
                        <FormControlLabel value="Tight" control={<Radio sx={{ color: "#C4A574" }} />} label={<Typography sx={{ color: "#bbb" }}>Tight</Typography>} />
                        <FormControlLabel value="Regular" control={<Radio sx={{ color: "#C4A574" }} />} label={<Typography sx={{ color: "#bbb" }}>Regular</Typography>} />
                        <FormControlLabel value="Loose" control={<Radio sx={{ color: "#C4A574" }} />} label={<Typography sx={{ color: "#bbb" }}>Loose</Typography>} />
                      </RadioGroup>
                    </FormControl>
                  </Grid>

                  <Grid item xs={12} className="responsive">
                    <TextField fullWidth multiline rows={4} label="Special Notes" value={formData.SpecialNotes} onChange={(e) => handleChange("SpecialNotes", e.target.value)} sx={fieldSx} />
                  </Grid>
                </Grid>
              </CardContent>
            </Card>

            <Box sx={{ display: "flex", gap: 2, justifyContent: "flex-end", mb: 6 }}>
              <Button variant="outlined" onClick={() => navigate("/admin/customers")} sx={{ borderColor: "rgba(255,255,255,0.06)", color: "#fff" }}>
                Cancel
              </Button>

              <Button type="submit" variant="contained" startIcon={saving ? <CircularProgress size={18} color="inherit" /> : <SaveIcon />} disabled={saving} sx={{ bgcolor: "#C4A574", color: "#000", fontWeight: 700, "&:hover": { bgcolor: "#B39562" } }}>
                {saving ? "Saving..." : isEdit ? "Update" : "Create"}
              </Button>
            </Box>
          </Box>
        </Box>
      </Box>
    </AppLayout>
  );
}
